import { defineStore, storeToRefs } from "pinia";
import * as Sentry from "@sentry/vue";
import { computed, Ref, ref, watch } from "vue";
import _ from "lodash";

import { api } from "@/lib/openapi";
import { RouteParamsRaw, useRouter } from "vue-router";
import { useToast } from "primevue/usetoast";
import { useOrganizationManager } from "@/composables/organization-manager";
import {
    type ArtefactOPFSIdentity,
    ArtefactOPFSFile,
} from "@/lib/ArtefactOPFSFile";
import { useStekManager } from "@/composables/stek-manager";
import { useCurrentEntityManager } from "@/stores/entity-store";
import { mime, downloadBlob } from "@/utils";
import { useConfirm } from "primevue/useconfirm";
import { useEditManager } from "@/composables/edit-manager";
import { syncChannel } from "@/lib/broadcast-channels";
import { EntityType } from "@/stores/entity-store";

export const targetPerEntity = () => ({
    [EntityType.DOMAIN]: api.startDomainDomainMgtUpdateDomainStartPost,
    [EntityType.PRODUCT]: api.startProductProductMgtUpdateProductStartPost,
    [EntityType.PROCESS]: api.startProcessProcessMgtUpdateProcessStartPost,
    [EntityType.ROLE]: api.startDomainRoleMgtUpdateRoleStartPost,
    [EntityType.TASK]: api.startProcessProcessElementMgtUpdateTaskStartPost,
    [EntityType.GATEWAY]:
        api.startProcessProcessElementMgtUpdateGatewayStartPost,
    [EntityType.EVENT]: api.startProcessProcessElementMgtUpdateEventStartPost,
    [EntityType.FLOW]: api.startProcessProcessElementMgtUpdateFlowStartPost,
    [EntityType.DOCUMENT]:
        api.startBizArtefactManagementUpdateArtefactStartPost,
});
export type SyncableEntityType = keyof ReturnType<typeof targetPerEntity>;
export type SyncEntityTypeFunction<T extends SyncableEntityType> = ReturnType<
    typeof targetPerEntity
>[T];
export type SyncReturn = Awaited<
    ReturnType<ReturnType<typeof useSyncStore>["sync"]>
>;

/**
 * Get the SyncableEntityType by string or return null.
 */
function getEntityType(entityType: string): SyncableEntityType | undefined {
    const entityTypes = Object.keys(targetPerEntity());
    if (entityTypes.includes(entityType)) {
        return entityType as SyncableEntityType;
    }
}

type ValidIdentities =
    | "domain_id"
    | "product_id"
    | "role_id"
    | "process_id"
    | "task_id"
    | "gateway_id"
    | "event_id"
    | "flow_id"
    | "artefact_id";

// Extract the valid identity key from the arguments from the api function for a specific entity type.
type EntityIdentityKey<T extends SyncableEntityType> = Extract<
    keyof Parameters<SyncEntityTypeFunction<T>>[0]["body"],
    ValidIdentities
>;

type SyncIdentity<T extends SyncableEntityType = SyncableEntityType> = Omit<
    ArtefactOPFSIdentity<T>,
    "mimetype"
>;

type IdentityPerEntity = {
    [T in SyncableEntityType]: EntityIdentityKey<T>;
};

const identityPerEntity: IdentityPerEntity = {
    domain: "domain_id",
    product: "product_id",
    role: "role_id",
    process: "process_id",
    task: "task_id",
    gateway: "gateway_id",
    event: "event_id",
    flow: "flow_id",
    document: "artefact_id",
};

// Inactivity time before a sync is triggered on an entity.
export const INACTIVITY_TIMEOUT_MS = 5 * 1000;

/**
 * Get the identity key by SyncableEntityType.
 */
function getEntityIdentityKey<T extends SyncableEntityType>(
    entityType: T
): EntityIdentityKey<T> {
    return identityPerEntity[entityType];
}

export type EntityArgTypes<T extends SyncableEntityType> = Parameters<
    SyncEntityTypeFunction<T>
>[0]["body"];

export type PagePerEntityAttributes = {
    [T in SyncableEntityType]: {
        [K in keyof EntityArgTypes<T>]?: string;
    };
};

export const pagePerEntityAttribute: PagePerEntityAttributes = {
    domain: {
        description: "domainPage",
    },
    product: {
        description: "productPage",
    },
    role: {
        description: "rolePage",
    },
    process: {
        description: "processPage",
        process_definition: "processDefinitionPage",
    },
    task: {
        description: "taskPage",
    },
    gateway: {
        description: "gatewayPage",
    },
    event: {
        description: "eventPage",
    },
    flow: {
        description: "flowPage",
    },
    document: {
        content: "documentPage",
    },
};

/**
 * An entry which defines what to sync and includes status and callbacks.
 */
type SyncEntry<T extends SyncableEntityType> = {
    // Arguments to provide when syncing.
    args: Parameters<SyncEntityTypeFunction<T>>;
    // Whether this entry is either being synced or has been synced.
    isHandled: boolean;
    // Whether this entry has successfully been synced.
    success?: boolean;
};

type SyncOpts<T extends SyncableEntityType> = {
    entityType: T;
} & Pick<SyncEntry<T>, "args">;

type SyncEntriesPerPage = {
    [T in SyncableEntityType]?: {
        [identity: string]: SyncEntry<T>[];
    };
};

type FailedSync<T extends SyncableEntityType> = {
    identity: SyncIdentity<T>;
    page: {
        name: string;
        params: RouteParamsRaw;
    };
    actions: {
        download?: () => void;
        discard?: (event: Event) => void;
    };
};

export const useSyncStore = defineStore("sync", ({ action }) => {
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();
    const { entity } = useCurrentEntityManager();
    const { isEditing } = useEditManager();
    const entries: Ref<SyncEntriesPerPage> = ref({});
    const persistedFailedSyncs: Ref<FailedSync<SyncableEntityType>[]> = ref([]);
    const failedSyncs: Ref<FailedSync<SyncableEntityType>[]> = ref([]);

    // When there are multiple 'failed syncs' for which the user restores their drafts,
    // any time the user restores the draft the list of to be ignored failed syncs should be
    // updated, so that the user action isn't for
    const identitiesToIgnoreUntilSync: Ref<SyncIdentity[]> = ref([]);

    const confirm = useConfirm();
    const router = useRouter();
    const toast = useToast();

    /**
     * Creates a failed synchronization entry for a given entity identity.
     *
     * The resulting object contains information about the entity, the page to navigate for editing,
     * and actions to either download the entity's file or discard it. If no page can be determined
     * for the given entity and attribute, it returns undefined and logs a warning.
     */
    const createFailedSync = <T extends SyncableEntityType>(
        identity: SyncIdentity<T>
    ) => {
        const pageName =
            pagePerEntityAttribute[identity.entityType][identity.attribute];
        if (!pageName) {
            console.warn(
                `No page found to edit ${identity.entityType} ${identity.attribute}`
            );
            return;
        }

        let actions: FailedSync<T>["actions"] = {};

        if (Object.hasOwn(identity, "mimetype")) {
            const artefactIdentity = identity as ArtefactOPFSIdentity<T>;

            actions = {
                download: async () => {
                    const storageArtefact =
                        await ArtefactOPFSFile.get(artefactIdentity);
                    if (!storageArtefact) {
                        const identityHash =
                            ArtefactOPFSFile.getIdentityHash(artefactIdentity);
                        Sentry.captureException(
                            new Error(
                                `Failed to get file for '${identityHash}'`
                            )
                        );
                        return;
                    }

                    const file = await storageArtefact.handle!.getFile();
                    const extension = mime.getExtension(
                        artefactIdentity.mimetype
                    );
                    downloadBlob(
                        URL.createObjectURL(file),
                        `file.${extension}`
                    );
                },
                discard: (event: Event) => {
                    confirm.require({
                        target: event.currentTarget as HTMLElement,
                        message: `
                            Are you sure you want to discard this file?
                        `,
                        icon: "pi pi-exclamation-triangle",
                        rejectProps: {
                            label: "Cancel",
                            severity: "secondary",
                            outlined: true,
                        },
                        acceptProps: {
                            label: "Discard",
                            severity: "danger",
                        },
                        accept: async () => {
                            const file =
                                await ArtefactOPFSFile.get(artefactIdentity);
                            if (file) {
                                file.remove();
                            }

                            failedSyncs.value = failedSyncs.value.filter(
                                (failedSync) =>
                                    failedSync.identity.entityId !==
                                    artefactIdentity.entityId
                            );
                            resetFailedSyncs();
                        },
                    });
                },
            };
        }

        return {
            identity,
            page: {
                name: pageName,
                params: {
                    orgName: organization.value!.name,
                    stekName: stek.value!.name,
                    [_.camelCase(getEntityIdentityKey(identity.entityType))]:
                        identity.entityId,
                },
            },
            actions,
        };
    };

    /**
     * Resets the list of failed synchronization attempts, optionally excluding a specific identity.
     *
     * This function attempts to reset the list of failed sync operations by fetching
     * entity identities from persistent storage and excluding any that should be ignored
     * until they are explicitly synchronized.
     */
    const resetFailedSyncs = action(async (identityToSkip?: SyncIdentity) => {
        if (identityToSkip) {
            identitiesToIgnoreUntilSync.value.push(identityToSkip);
        }

        if (organization.value === undefined || stek.value === undefined) {
            persistedFailedSyncs.value = [];
            return;
        }

        const identities = (
            await ArtefactOPFSFile.getEntryIdentitiesForOrganization(
                organization.value.identity
            )
        ).filter(({ entityId, attribute }) => {
            if (
                identitiesToIgnoreUntilSync.value.some(
                    (identity) =>
                        identity.entityId === entityId &&
                        identity.attribute === attribute
                )
            ) {
                return false;
            }

            return true;
        });

        persistedFailedSyncs.value = identities
            .map(createFailedSync)
            .filter((failedSync) => failedSync !== undefined);
    });

    watch(
        [() => organization.value, () => stek.value],
        async () => {
            resetFailedSyncs();
        },
        { immediate: true }
    );

    const showSyncFailureToast = () => {
        toast.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to sync your changes.",
        });
    };

    /**
     * Trigger a sync for all queued operations.
     *
     * When an entityIdToSync is provided, it will only sync that particular entity.
     */
    const sync = action(
        async (entityIdToSync?: string): Promise<SyncIdentity[]> => {
            const fullySyncedIdentities: SyncIdentity[] = [];
            const requests: Promise<void>[] = [];
            const discardPromises: ReturnType<SyncIdentity["remove"]>[] = [];

            // Use a copy to not interfere with the loop, as we remove entities.
            const entriesCopy = _.cloneDeep(entries.value);
            for (const entityType in entriesCopy) {
                const currentEntityType = entityType as SyncableEntityType;
                for (const entityId in entriesCopy[currentEntityType]) {
                    // When a specific entity id is provided, skip syncing other
                    // entities.
                    if (entityIdToSync && entityId !== entityIdToSync) {
                        continue;
                    }

                    const syncEntries =
                        entriesCopy[currentEntityType][entityId];
                    delete entries.value[currentEntityType]?.[entityId];

                    if (!syncEntries || syncEntries.length === 0) {
                        continue;
                    }

                    let combinedArgs: EntityArgTypes<typeof currentEntityType> =
                        syncEntries[0].args[0].body;
                    const handledPointers: SyncEntry<
                        typeof currentEntityType
                    >[] = [];
                    for (const call of syncEntries) {
                        // No need to handle the same call twice.
                        if (call.isHandled) {
                            continue;
                        }

                        combinedArgs = _.merge(combinedArgs, call.args[0].body);
                        call.isHandled = true;
                        handledPointers.push(call);
                    }

                    // No need to save anything if we have no changes.
                    if (handledPointers.length === 0) {
                        continue;
                    }

                    requests.push(
                        targetPerEntity()
                            [currentEntityType]({ body: combinedArgs })
                            .then(() => {
                                const success = true;
                                for (const entry of handledPointers) {
                                    entry.success = success;
                                }

                                const identities: SyncIdentity[] = [];
                                const identityKey =
                                    getEntityIdentityKey(currentEntityType);
                                for (const arg in combinedArgs) {
                                    if (arg === identityKey) {
                                        continue;
                                    }

                                    // Not sure how to infer blob here, so we cast it.
                                    const value = combinedArgs[
                                        arg as keyof EntityArgTypes<
                                            typeof currentEntityType
                                        >
                                    ] as Blob | string;

                                    if (value === undefined) {
                                        // Should never happen, record if it does.
                                        Sentry.captureException(
                                            new Error(
                                                `No file found for ${arg}`
                                            )
                                        );
                                        continue;
                                    }

                                    const identity = {
                                        organizationId:
                                            organization.value!.identity,
                                        entityType:
                                            currentEntityType as SyncableEntityType,
                                        entityId: entityId,
                                        attribute: arg as keyof EntityArgTypes<
                                            typeof currentEntityType
                                        >,
                                    };

                                    identities.push(identity);
                                    syncChannel.postMessage({
                                        message: "synced",
                                        identity,
                                    });

                                    if (value instanceof Blob) {
                                        discardPromises.push(
                                            ArtefactOPFSFile.get({
                                                ...identity,
                                                mimetype: value.type,
                                            }).then((artefactOPFS) =>
                                                artefactOPFS?.remove()
                                            )
                                        );
                                    }
                                }

                                // Check if we received some new sync entries for this entity. If so, we are not
                                // 'fully synced' yet. The user might still be editing a related entity.
                                //
                                // Only after a full sync, depending services should be allowed to act on this information.
                                // E.g. the draft manager is allowed to discard a draft since the sync has completed.
                                fullySyncedIdentities.push(...identities);
                            })
                            .catch((error) => {
                                Sentry.captureException(error);
                                const success = false;
                                for (const entry of handledPointers) {
                                    entry.success = success;
                                }
                                showSyncFailureToast();

                                const _failedSyncs: FailedSync<SyncableEntityType>[] =
                                    Object.keys(combinedArgs)
                                        .map((name) => {
                                            const identity = {
                                                organizationId:
                                                    organization.value!
                                                        .identity,
                                                entityType:
                                                    currentEntityType as SyncableEntityType,
                                                entityId: entityId,
                                                attribute:
                                                    name as keyof EntityArgTypes<
                                                        typeof currentEntityType
                                                    >,
                                            };
                                            return createFailedSync(identity);
                                        })
                                        .filter(
                                            (failedSync) =>
                                                failedSync !== undefined
                                        );
                                failedSyncs.value.push(..._failedSyncs);
                            })
                    );
                }
            }

            await Promise.allSettled(requests);
            await Promise.allSettled(discardPromises);

            resetFailedSyncs().then(() => {
                identitiesToIgnoreUntilSync.value =
                    identitiesToIgnoreUntilSync.value.filter(
                        (identity) =>
                            !fullySyncedIdentities.find(
                                (syncedIdentity) =>
                                    syncedIdentity.entityId ===
                                        identity.entityId &&
                                    syncedIdentity.attribute ===
                                        identity.attribute
                            )
                    );
            });

            return fullySyncedIdentities;
        }
    );

    const entitySyncTimers: Record<string, ReturnType<typeof setTimeout>> = {};

    /**
     * Debounces synchronization for an entity after inactivity.
     *
     * This function schedules a sync operation for the given entity ID,
     * resetting the inactivity timer if it's called again within the delay period.
     * Ensures that the sync is only performed after a specified period of inactivity.
     *
     * @param {string} entityId - The ID of the entity to be synchronized after inactivity.
     */
    const debounceEntitySync = async (entityId: string) => {
        if (entitySyncTimers[entityId]) {
            clearTimeout(entitySyncTimers[entityId]);
        }

        entitySyncTimers[entityId] = setTimeout(async () => {
            await sync(entityId);
        }, INACTIVITY_TIMEOUT_MS);
    };

    /**
     * Queues a synchronization operation for the given entity type and arguments.
     *
     * This function queues a sync request for a specific entity, ensuring that
     * only one sync operation per entity is handled at a time, while any further
     * operations are batched. The sync operation is deferred using a debouncing mechanism
     * to minimize redundant operations during rapid changes.
     */
    const queueSync = action(
        async <T extends SyncableEntityType>({
            entityType: entityType_,
            args,
        }: SyncOpts<T>): Promise<void> => {
            const entityType = getEntityType(entityType_);
            if (!entityType) {
                throw new Error(`Unknown entity type ${entityType}`);
            }

            const identityKey = getEntityIdentityKey(entityType);
            const identity = args[0]["body"][identityKey];

            if (!identity) {
                throw new Error(`No identity found for ${entityType}`);
            }

            syncChannel.postMessage({
                message: "queued",
                identity: {
                    organizationId: organization.value!.identity,
                    entityType: entityType,
                    entityId: identity,
                },
            });

            const saveEntry: SyncEntry<T> = {
                args,
                isHandled: false,
            };

            if (!entries.value[entityType]) {
                entries.value[entityType] = {};
            }

            if (!entries.value[entityType][identity]) {
                entries.value[entityType][identity] = [];
            }

            (entries.value[entityType][identity] as SyncEntry<T>[]).push(
                saveEntry
            );

            debounceEntitySync(identity);
        }
    );

    // Returns whether a sync operation is currently being executed.
    const isBeingSynced = computed(() => {
        for (const syncEntries of Object.values(entries.value)) {
            for (const entriesPerIdentity of Object.values(syncEntries)) {
                for (const entry of entriesPerIdentity) {
                    if (entry.isHandled && !("success" in entry)) {
                        return true;
                    }
                }
            }
        }
        return false;
    });

    // Returns false if anything is not synced yet, if currently syncing or a sync has failed.
    const isInSync = computed(() => {
        for (const syncEntries of Object.values(entries.value)) {
            for (const entriesPerIdentity of Object.values(syncEntries)) {
                for (const entry of entriesPerIdentity) {
                    if (!("success" in entry)) {
                        return false;
                    }
                }
            }
        }
        return true;
    });

    /**
     * Trigger a sync when you leave edit mode.
     */
    watch(isEditing, (editing) => {
        if (!editing) {
            sync();
        }
    });

    /**
     * Quickly fire a sync before you leave.
     */
    window.onbeforeunload = () => {
        // Cannot await sync here, since that would trigger the browser popup.
        sync();
    };

    /**
     * Fire a sync event when the window looses focus.
     */
    window.addEventListener("blur", async () => {
        await sync();
    });

    /**
     * Optimistically sync the current entity on a route change.
     */
    router.beforeEach(() => {
        if (entity.value?.identity) {
            sync(entity.value?.identity);
        }
    });

    return {
        queueSync,
        isInSync,
        isBeingSynced,
        failedSyncs: computed(() => {
            return [...persistedFailedSyncs.value, ...failedSyncs.value];
        }),
        sync,
        resetFailedSyncs,
    };
});

export const useSyncManager = () => storeToRefs(useSyncStore());
